<template>
  <div class="upgrade-blocked-modal">
    <div class="icon-container">
      <i class="hgi-stroke hgi-alert-02 icon" />
    </div>
    <h3 class="title">Routine cannot upgrade to a new version</h3>
    <template v-if="reason === 'read-only-volume'">
      <p class="description">
        Routine is running from a read-only location, usually straight from the
        downloaded disk image, so upgrades cannot be installed.
      </p>
      <ol class="steps">
        <li>Quit Routine</li>
        <li>
          Drag <strong>Routine</strong> from the disk image (or your
          <strong>Downloads</strong> folder) into your
          <strong>Applications</strong> folder
        </li>
        <li>Relaunch Routine from <strong>Applications</strong></li>
      </ol>
    </template>
    <template v-else-if="reason === 'updater-missing'">
      <p class="description">
        Routine's updater program is missing from this installation — an
        antivirus may have removed it, or Routine was copied instead of being
        installed.
      </p>
      <ol class="steps">
        <li>
          Reinstall <strong>Routine</strong> from the download page to restore
          the updater
        </li>
        <li>
          If it happens again, allow <strong>Routine</strong> in your antivirus
          settings, or ask your IT administrator to add an exception
        </li>
      </ol>
    </template>
    <template v-else>
      <p class="description">
        The upgrade process was blocked from starting (usually by an antivirus
        or a security policy on this computer).
      </p>
      <ol class="steps">
        <li>
          Allow <strong>Routine</strong> in your antivirus settings, or ask your
          IT administrator to add an exception
        </li>
        <li>
          Until then, you can install new versions manually from the download
          page
        </li>
      </ol>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

import type { UpgradeBlockedReason } from "@/upgrade/upgrader";

export default defineComponent({
  props: {
    reason: {
      type: String as PropType<UpgradeBlockedReason>,
      required: true,
    },
  },
});
</script>

<style scoped lang="scss">
.upgrade-blocked-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 48px;
  max-width: 520px;
  text-align: center;

  .icon-container {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    @include themed() {
      background: t($callout-alert);
    }

    .icon {
      font-size: 24px;
      @include themed() {
        color: t($secondary);
      }
    }
  }

  .title {
    @extend %font-h3;
    margin: 0 0 20px;
    @include themed() {
      color: t($primary);
    }
  }

  .description {
    @extend %font-body-regular;
    margin: 0 0 20px;
    line-height: 1.5;
    text-align: start;
    width: 100%;
    @include themed() {
      color: t($grey-60);
    }
  }

  .steps {
    text-align: start;
    padding-inline-start: 28px;
    margin: 12px 0 0;
    width: 100%;

    li {
      @extend %font-body-regular;
      line-height: 1.8;
      padding-inline-start: 4px;
      @include themed() {
        color: t($primary);
      }

      &:not(:last-child) {
        margin-bottom: 8px;
      }
    }
  }
}
</style>
